import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  doc,
  getDoc,
  setDoc,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Search Service for Journey Page Feature
 * Handles user search and public ideas filtering
 */

/**
 * Search users by name or email
 * @param {string} searchQuery - Search term
 * @returns {Promise<Array>} Array of user objects
 */
export const searchUsers = async (searchQuery) => {
  try {
    if (!searchQuery || searchQuery.trim().length < 2) {
      return [];
    }

    const searchTerm = searchQuery.trim().toLowerCase();
    const usersRef = collection(db, 'users');

    // Get ALL users and filter client-side for case-insensitive search
    // Removed limit to ensure all users are searchable
    const q = query(usersRef);
    const snapshot = await getDocs(q);

    const results = [];
    snapshot.forEach((doc) => {
      const userData = doc.data();
      const name = (userData.authorName || '').toLowerCase();
      const email = (userData.email || '').toLowerCase();

      // Check if search term matches name or email (partial match)
      if (name.includes(searchTerm) || email.includes(searchTerm)) {
        results.push({
          id: doc.id,
          authorName: userData.authorName || userData.email,
          email: userData.email,
          profileImage: userData.profileImage || null,
          pulse: userData.pulse || 0,
          bio: userData.bio || "" // ✅ Include bio
        });
      }
    });

    // Sort by relevance (exact matches first, then partial matches)
    results.sort((a, b) => {
      const aName = (a.authorName || '').toLowerCase();
      const bName = (b.authorName || '').toLowerCase();
      const aEmail = (a.email || '').toLowerCase();
      const bEmail = (b.email || '').toLowerCase();

      // Exact name match gets highest priority
      const aExactName = aName === searchTerm ? 0 : 1;
      const bExactName = bName === searchTerm ? 0 : 1;
      if (aExactName !== bExactName) return aExactName - bExactName;

      // Name starts with search term gets second priority
      const aStartsName = aName.startsWith(searchTerm) ? 0 : 1;
      const bStartsName = bName.startsWith(searchTerm) ? 0 : 1;
      if (aStartsName !== bStartsName) return aStartsName - bStartsName;

      // Email match gets third priority
      const aEmailMatch = aEmail.includes(searchTerm) ? 0 : 1;
      const bEmailMatch = bEmail.includes(searchTerm) ? 0 : 1;
      if (aEmailMatch !== bEmailMatch) return aEmailMatch - bEmailMatch;

      // Alphabetical order as final tiebreaker
      return aName.localeCompare(bName);
    });

    // Return top 15 results for better coverage
    return results.slice(0, 15);
  } catch (error) {
    console.error('Error searching users:', error);
    throw error;
  }
};

/**
 * Get user data by ID
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} User data, or null if unavailable
 * (not found, or blocked by Firestore rules for a logged-out caller)
 */
export const getUserById = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      return {
        id: userSnap.id,
        ...userSnap.data()
      };
    } else {
      throw new Error('User not found');
    }
  } catch (error) {
    // "users" requires sign-in in Firestore rules, so a logged-out caller
    // (e.g. the public idea feed / ViewIdeaPage) will always hit
    // permission-denied here. That's an expected state, not a bug —
    // return null quietly instead of throwing, so callers can just show
    // a fallback author name. Any other error (network, etc.) still throws.
    if (error?.code === 'permission-denied') {
      return null;
    }
    console.error('Error fetching user:', error);
    throw error;
  }
};

/**
 * Get public ideas posted by a specific user
 * IMPORTANT: Only returns public ideas, excludes competition ideas
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of public idea objects
 */
export const getPublicIdeasByUser = async (userId) => {
  try {
    const COLLECTIONS = ['postedIdeas', 'publicIdeas', 'competitionIdeas', 'PostedCompetitionIdeas'];
    const seen = new Set();
    const ideas = [];

    for (const coll of COLLECTIONS) {
      try {
        const q1 = query(collection(db, coll), where('ownerId', '==', userId));
        const q2 = query(collection(db, coll), where('userId', '==', userId));
        const q3 = query(collection(db, coll), where('uid', '==', userId));

        const snapshots = await Promise.all([
          getDocs(q1).catch(() => ({ docs: [] })),
          getDocs(q2).catch(() => ({ docs: [] })),
          getDocs(q3).catch(() => ({ docs: [] }))
        ]);

        snapshots.forEach(s => {
          s.docs.forEach((docSnap) => {
            if (seen.has(docSnap.id)) return;
            seen.add(docSnap.id);
            const data = docSnap.data();
            ideas.push({
              id: docSnap.id,
              title: data.title,
              description: data.description,
              tags: data.tags || [],
              imagePath: data.imagePath || data.image || null,
              createdAt: data.createdAt,
              authorId: data.ownerId || data.userId || data.uid,
              authorName: data.authorName,
              likes: data.likes || 0,
              comments: data.comments || 0,
              evolveCount: data.evolveCount || 0,
              stage: data.stage || 'Spark',
              _coll: coll
            });
          });
        });
      } catch (e) {
        console.warn(`Error searching ${coll}:`, e);
      }
    }

    // Sort combined
    ideas.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));

    return ideas;
  } catch (error) {
    console.error('getPublicIdeasByUser error:', error);
    return [];
  }
};

/**
 * Save an idea for a user
 * @param {string} userId - ID of the user saving the idea
 * @param {string} ideaId - ID of the idea to save
 */
export const saveIdea = async (userId, ideaId) => {
  try {
    const savedRef = doc(db, 'users', userId, 'savedIdeas', ideaId);
    // We save a timestamp or simple marker
    await setDoc(savedRef, {
      savedAt: new Date(),
      ideaId: ideaId
    });
    return true;
  } catch (error) {
    console.error('Error saving idea:', error);
    throw error;
  }
};

/**
 * Unsave an idea for a user
 * @param {string} userId - ID of the user unsaving the idea
 * @param {string} ideaId - ID of the idea to unsave
 */
export const unsaveIdea = async (userId, ideaId) => {
  try {
    const savedRef = doc(db, 'users', userId, 'savedIdeas', ideaId);
    await deleteDoc(savedRef);
    return true;
  } catch (error) {
    console.error('Error unsaving idea:', error);
    throw error;
  }
};

/**
 * Check if an idea is saved by the user
 * @param {string} userId
 * @param {string} ideaId
 * @returns {Promise<boolean>}
 */
export const isIdeaSaved = async (userId, ideaId) => {
  try {
    const savedRef = doc(db, 'users', userId, 'savedIdeas', ideaId);
    const snap = await getDoc(savedRef);
    return snap.exists();
  } catch (error) {
    console.error('Error checking saved status:', error);
    return false;
  }
};

/**
 * Get all saved ideas for a user
 * @param {string} userId
 * @returns {Promise<Array>} Array of idea objects
 */
export const getSavedIdeas = async (userId) => {
  try {
    // 1. Get all saved IDs
    const savedRef = collection(db, 'users', userId, 'savedIdeas');
    const savedSnap = await getDocs(savedRef);

    if (savedSnap.empty) return [];

    const savedIdeaIds = savedSnap.docs.map(doc => doc.id);

    // 2. Fetch actual idea details from postedIdeas
    const ideas = [];
    // Firestore 'in' query supports up to 10 items. For robustness, we might need batching or individual fetches.
    // For now, let's do Promise.all for individual fetches to ensure we support >10 items and avoid complexity.
    // Optimisation: In a real large app, we'd paginate or batch.

    await Promise.all(savedIdeaIds.map(async (id) => {
      try {
        const ideaRef = doc(db, 'postedIdeas', id);
        const ideaSnap = await getDoc(ideaRef);
        if (ideaSnap.exists()) {
          const ideaData = ideaSnap.data();
          ideas.push({
            id: ideaSnap.id,
            title: ideaData.title,
            description: ideaData.description,
            tags: ideaData.tags || [],
            imagePath: ideaData.imagePath || ideaData.image || null,
            createdAt: ideaData.createdAt,
            authorId: ideaData.ownerId || ideaData.userId,
            authorName: ideaData.authorName,
            likes: ideaData.likes || 0,
            comments: ideaData.comments || 0,
            competitionId: ideaData.competitionId || null // Include this just in case
          });
        }
      } catch (e) {
        console.warn(`Could not fetch idea ${id}`, e);
      }
    }));

    // Sort by recent? (Optional, currently random order due to Promise.all)
    return ideas;

  } catch (error) {
    console.error('Error fetching saved ideas:', error);
    throw error;
  }
};

export default {
  searchUsers,
  getUserById,
  getPublicIdeasByUser,
  saveIdea,
  unsaveIdea,
  isIdeaSaved,
  getSavedIdeas
};
